import { useEffect } from 'react';

import { useForm } from '@tanstack/react-form';
import { createFileRoute } from '@tanstack/react-router';
import {
  Button,
  FormField,
  InputNumber,
  InputText,
  SubmitButtonWrapper,
  TextBlock,
  toast,
} from '@skillarea/lib-frontend';
import {
  CRYPTOCLOUD_USDT_NETWORKS,
  EWithdrawalKycStatus,
} from '@skillarea/lib-shared';
import { useTranslation } from 'react-i18next';

import cx from 'classnames';

import {
  DEFAULT_NETWORK,
  getResultErrorMessage,
  NetworkSelect,
  toCoinsNumber,
} from '../-acquiring-shared';
import { accountAuthGuard } from '../-account-auth-guard';
import styles from '../acquiring.module.scss';

import { AccountLayout } from '~/components/Account/AccountLayout';
import { LayoutCard } from '~/components/LayoutCard/LayoutCard';
import { centsToUsd, pushGa4Event } from '~/lib/analytics/gtm';
import { useUserMfaEnabled } from '~/stores/userStore';
import { BillingTaskService } from '~/services/billingTaskService';
import { useEnableTotpFlow } from '~/hooks/useEnableTotpFlow';
import { EnableTotpModal } from '~/components/Account/EnableTotpModal';
import { useWithdrawalKycFlow } from '~/hooks/useWithdrawalKycFlow';
import { useWithdrawalKycStore } from '~/stores/withdrawalKycStore';
import { MinimumAgeModal } from '~/components/MinimumAgeModal/MinimumAgeModal';
import { useMinimumAgeGuard } from '~/hooks/useMinimumAgeGuard';
import { translateBillingError } from '~/lib/billingLocale';

const billingTaskService = new BillingTaskService();

function pushSuccessfulWithdrawalEvent(coins: number): void {
  pushGa4Event('withdrawal_success', {
    withdrawal_amount: centsToUsd(coins),
    valueInCents: coins,
  });
}

export const Route = createFileRoute('/account/balance/withdrawal')({
  component: WithdrawalPage,
  beforeLoad: ({ context }) => accountAuthGuard({ context }),
});

function WithdrawalPage() {
  const { t } = useTranslation('billing');
  const {
    closeMinimumAgeModal,
    isMinimumAgeModalOpen,
    isUnderMinimumAge,
    minimumAge,
    showMinimumAgeModal,
  } = useMinimumAgeGuard();
  const isMfaEnabled = useUserMfaEnabled();

  const {
    closeModal,
    code,
    confirmEnableTotp,
    isLoading,
    isModalOpen,
    openEnableTotp,
    setCode,
  } = useEnableTotpFlow();

  const { loadStatus, refreshStatus, startOrContinueKyc } =
    useWithdrawalKycFlow();
  const kycStatus = useWithdrawalKycStore((s) => s.status);
  const kycIsLoading = useWithdrawalKycStore((s) => s.isLoading);
  const kycCanStart = useWithdrawalKycStore((s) => s.canStart);
  const kycCanContinue = useWithdrawalKycStore((s) => s.canContinue);

  const isWithdrawalBlockedByAge = isUnderMinimumAge;
  const isKycApproved = kycStatus === EWithdrawalKycStatus.approved;
  const isFormEnabled =
    !isWithdrawalBlockedByAge && isMfaEnabled && isKycApproved;

  useEffect(() => {
    if (isWithdrawalBlockedByAge) {
      showMinimumAgeModal();
    }
  }, [isWithdrawalBlockedByAge, showMinimumAgeModal]);

  useEffect(() => {
    if (isMfaEnabled) {
      void loadStatus();
    }
  }, [isMfaEnabled, loadStatus]);

  const withdrawForm = useForm({
    defaultValues: {
      coins: 0,
      currencyCode: DEFAULT_NETWORK,
      toAddress: '',
      code: '',
    },
    onSubmit: async ({ value }) => {
      if (isWithdrawalBlockedByAge) {
        showMinimumAgeModal();
        return;
      }

      const coins = toCoinsNumber(value.coins);
      if (coins === null) {
        toast?.error(t('withdrawal.toast.amountPositive'));
        return;
      }

      const result = await billingTaskService.createCryptoWithdrawTask({
        body: {
          coins,
          currencyCode: String(value.currencyCode),
          toAddress: String(value.toAddress).trim(),
          code: String(value.code).trim(),
        },
      });

      if (!result.success) {
        toast?.error(translateBillingError(t, getResultErrorMessage(result)));
        return;
      }

      pushSuccessfulWithdrawalEvent(coins);
      toast?.success(t('withdrawal.toast.withdrawCreated'));
    },
  });

  const paypalWithdrawForm = useForm({
    defaultValues: {
      coins: 0,
      paypalEmail: '',
      code: '',
    },
    onSubmit: async ({ value }) => {
      if (isWithdrawalBlockedByAge) {
        showMinimumAgeModal();
        return;
      }

      const coins = toCoinsNumber(value.coins);
      if (coins === null) {
        toast?.error(t('withdrawal.toast.amountPositive'));
        return;
      }

      const result = await billingTaskService.createPaypalWithdrawTask({
        body: {
          coins,
          paypalEmail: String(value.paypalEmail).trim(),
          code: String(value.code).trim(),
        },
      });

      if (!result.success) {
        toast?.error(translateBillingError(t, getResultErrorMessage(result)));
        return;
      }

      pushSuccessfulWithdrawalEvent(coins);
      toast?.success(t('withdrawal.toast.paypalPayoutCreated'));
    },
  });

  return (
    <AccountLayout className={styles.content}>
      {!isFormEnabled && (
        <div data-testid="withdrawal-prerequisites">
          <LayoutCard title={t('withdrawal.securityRequirements')}>
            {isWithdrawalBlockedByAge ? (
              <div
                className={styles.securityNotice}
                data-testid="withdrawal-minimum-age-required"
              >
                <TextBlock color="contrast" weight="600">
                  {t('withdrawal.ageRestrictedTitle')}
                </TextBlock>
                <TextBlock color="secondary">
                  {t('withdrawal.ageRestrictedHint', { age: minimumAge })}
                </TextBlock>
              </div>
            ) : !isMfaEnabled ? (
              <div
                className={styles.securityNotice}
                data-testid="withdrawal-2fa-required"
              >
                <TextBlock color="contrast" weight="600">
                  {t('withdrawal.mfaRequiredTitle')}
                </TextBlock>
                <TextBlock color="secondary">
                  {t('withdrawal.mfaRequiredHint')}
                </TextBlock>
                <div className={styles.securityActions}>
                  <Button
                    color="accent"
                    data-testid="withdrawal-enable-2fa-button"
                    disabled={isLoading}
                    onClick={() => {
                      void openEnableTotp();
                    }}
                    size="s"
                    type="button"
                    variant="filled"
                  >
                    {t('withdrawal.enable2fa')}
                  </Button>
                </div>
              </div>
            ) : !isKycApproved ? (
              <div
                className={styles.securityNotice}
                data-testid="withdrawal-kyc-required"
              >
                <TextBlock color="contrast" weight="600">
                  {t('withdrawal.kycRequiredTitle')}
                </TextBlock>
                <TextBlock color="secondary">
                  {kycStatus === EWithdrawalKycStatus.pending
                    ? t('withdrawal.kycPending')
                    : kycStatus === EWithdrawalKycStatus.resubmission_requested
                      ? t('withdrawal.kycResubmission')
                      : t('withdrawal.kycRequired')}
                </TextBlock>
                <div className={styles.securityActions}>
                  {kycCanStart || kycCanContinue ? (
                    <Button
                      color="accent"
                      data-testid={
                        kycCanContinue
                          ? 'withdrawal-kyc-continue-button'
                          : 'withdrawal-kyc-start-button'
                      }
                      disabled={kycIsLoading}
                      onClick={() => {
                        void startOrContinueKyc();
                      }}
                      size="s"
                      type="button"
                      variant="filled"
                    >
                      {kycCanContinue
                        ? t('withdrawal.continueKyc')
                        : t('withdrawal.startKyc')}
                    </Button>
                  ) : null}
                  {kycStatus === EWithdrawalKycStatus.pending ||
                  kycStatus === EWithdrawalKycStatus.resubmission_requested ? (
                    <Button
                      color="secondary"
                      data-testid="withdrawal-kyc-refresh-button"
                      disabled={kycIsLoading}
                      onClick={() => {
                        void refreshStatus();
                      }}
                      size="s"
                      type="button"
                      variant="outline"
                    >
                      {t('withdrawal.refreshStatus')}
                    </Button>
                  ) : null}
                </div>
              </div>
            ) : null}
          </LayoutCard>
        </div>
      )}

      <LayoutCard
        className={styles.hide}
        data-testid="account-withdrawal-page"
        hideTitleDivider
        title={t('withdrawal.paypalTitle')}
      >
        <form
          className={cx(styles.formCard, !isFormEnabled && styles.blockedForm)}
          data-testid="paypal-withdrawal-form"
          onSubmit={(e) => {
            e.preventDefault();
            void paypalWithdrawForm.handleSubmit();
          }}
        >
          <TextBlock className={styles.formIntro} color="secondary">
            {t('withdrawal.paypalIntro')}
          </TextBlock>

          <fieldset className={styles.formFieldset} disabled={!isFormEnabled}>
            <div className={styles.field}>
              <TextBlock color="contrast" weight="600">
                {t('withdrawal.amountUsd')}
              </TextBlock>
              <FormField form={paypalWithdrawForm} name="coins">
                <InputNumber
                  allowDecimal
                  backgroundColor="rgba(3, 3, 4, 0.90)"
                  data-testid="balance-paypal-withdraw-coins-input"
                  placeholder={t('deposit.enterAmount')}
                  textColor="#fff"
                />
              </FormField>
            </div>

            <div className={styles.field}>
              <TextBlock color="contrast" weight="600">
                {t('withdrawal.paypalEmail')}
              </TextBlock>
              <FormField form={paypalWithdrawForm} name="paypalEmail">
                <InputText
                  backgroundColor="rgba(3, 3, 4, 0.90)"
                  data-testid="balance-paypal-withdraw-email-input"
                  placeholder={t('withdrawal.paypalEmailPlaceholder')}
                  textColor="#fff"
                />
              </FormField>
            </div>

            <div className={styles.field}>
              <TextBlock color="contrast" weight="600">
                {t('withdrawal.tfaCode')}
              </TextBlock>
              <FormField form={paypalWithdrawForm} name="code">
                <InputText
                  backgroundColor="rgba(3, 3, 4, 0.90)"
                  data-testid="balance-paypal-withdraw-2fa-input"
                  placeholder={t('withdrawal.tfaCodePlaceholder')}
                  textColor="#fff"
                />
              </FormField>
            </div>

            <SubmitButtonWrapper form={paypalWithdrawForm}>
              <Button
                className={cx(styles.primaryButton, styles.paypalPrimaryButton)}
                color="primary"
                data-testid="balance-paypal-withdraw-submit"
                size="s"
                type="submit"
                variant="filled"
              >
                {t('withdrawal.requestPaypalPayout')}
              </Button>
            </SubmitButtonWrapper>
          </fieldset>
        </form>
      </LayoutCard>

      <LayoutCard
        data-testid="account-withdrawal-page"
        title={t('withdrawal.cryptoTitle')}
      >
        <form
          className={cx(styles.formCard, !isFormEnabled && styles.blockedForm)}
          data-testid="withdrawal-form"
          onSubmit={(e) => {
            e.preventDefault();
            void withdrawForm.handleSubmit();
          }}
        >
          <fieldset className={styles.formFieldset} disabled={!isFormEnabled}>
            <div className={styles.field}>
              <TextBlock color="contrast" weight="600">
                {t('withdrawal.amountUsdt')}
              </TextBlock>
              <FormField form={withdrawForm} name="coins">
                <InputNumber
                  allowDecimal
                  backgroundColor="rgba(3, 3, 4, 0.90)"
                  data-testid="balance-withdraw-coins-input"
                  placeholder={t('deposit.enterAmount')}
                  textColor="#fff"
                />
              </FormField>
            </div>

            <div className={styles.field}>
              <TextBlock color="contrast" weight="600">
                {t('withdrawal.network')}
              </TextBlock>
              <FormField form={withdrawForm} name="currencyCode">
                <NetworkSelect
                  data-testid="balance-withdraw-network-select"
                  options={CRYPTOCLOUD_USDT_NETWORKS}
                />
              </FormField>
            </div>

            <div className={styles.field}>
              <TextBlock color="contrast" weight="600">
                {t('withdrawal.address')}
              </TextBlock>
              <FormField form={withdrawForm} name="toAddress">
                <InputText
                  backgroundColor="rgba(3, 3, 4, 0.90)"
                  data-testid="balance-withdraw-address-input"
                  placeholder={t('withdrawal.addressPlaceholder')}
                  textColor="#fff"
                />
              </FormField>
            </div>

            <div className={styles.field}>
              <TextBlock color="contrast" weight="600">
                {t('withdrawal.tfaCode')}
              </TextBlock>
              <FormField form={withdrawForm} name="code">
                <InputText
                  backgroundColor="rgba(3, 3, 4, 0.90)"
                  data-testid="balance-withdraw-2fa-input"
                  placeholder={t('withdrawal.tfaCodePlaceholder')}
                  textColor="#fff"
                />
              </FormField>
            </div>

            <SubmitButtonWrapper form={withdrawForm}>
              <Button
                className={styles.primaryButton}
                color="primary"
                data-testid="balance-withdraw-submit"
                size="s"
                type="submit"
                variant="filled"
              >
                {t('withdrawal.createCryptoWithdraw')}
              </Button>
            </SubmitButtonWrapper>
          </fieldset>
        </form>
      </LayoutCard>

      <TextBlock color="secondaryContrast">
        {t('withdrawal.payoutNotice')}
      </TextBlock>

      <EnableTotpModal
        code={code}
        isLoading={isLoading}
        isOpen={isModalOpen}
        onClose={closeModal}
        onCodeChange={setCode}
        onConfirm={() => {
          void confirmEnableTotp();
        }}
      />

      <MinimumAgeModal
        isOpen={isMinimumAgeModalOpen}
        minimumAge={minimumAge}
        onClose={closeMinimumAgeModal}
      />
    </AccountLayout>
  );
}
